

export const API_BASE    = process.env.NEXT_PUBLIC_API_URL    || "/api";
export const SERVER_BASE = process.env.NEXT_PUBLIC_SERVER_BASE || "";

export const FALLBACK = "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=600&q=80";

export const getToken = (): string => {
  if (typeof window === "undefined") return "";
  const KEYS = ["token", "authToken", "jwt", "access_token", "accessToken", "admin_token", "auth_token"];
  for (const k of KEYS) {
    const v = localStorage.getItem(k) ?? sessionStorage.getItem(k);
    if (v) return v;
  }
  return "";
};

export async function apiFetch<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { message?: string }).message ?? `HTTP ${res.status}`);
  return body as T;
}

export function resolveImg(src: string | undefined | null): string {
  if (!src) return FALLBACK;
  if (src.startsWith("http") || src.startsWith("data:")) return src;
  return `${SERVER_BASE}${src}`;
}

// ─── Types ──────────────────────────────────────────────────────────────────
export type MediaItem    = { type: "img"; src: string; alt?: string; is_primary?: boolean };
export type VariantSize  = { value: string; stock_qty: number };
export type VariantColorImage = {
  image_url:  string;
  alt_text?:  string;
  is_primary: boolean;
  sort_order: number;
};
export type VariantColor = { value: string; hex: string; stock_qty: number; images?: VariantColorImage[] };
export type VariantItem  = { size: string; color: string; hex: string; stock_qty: number };
export type Variants     = { sizes: VariantSize[]; colors: VariantColor[]; items: VariantItem[] };

export type DBCategory = {
  id:           number;
  name:         string;
  slug:         string;
  parent_id:    number | null;
  parent_name:  string | null;
  product_type: string;
  level:        number;
  status:       string;
  description:  string | null;
  image_url:    string | null;
  sort_order:   number;
};

export type Product = {
  id:             number;
  name:           string;
  slug:           string;
  sku:            string;
  category:       string;
  category_id:    number | null;
  category_slug?: string;
  parent_category?: string;
  short_description: string | null;
  material:       string | null;
  gender:         string | null;
  PRODUCT_TYPE_OPTIONS?: string | null;
  regular_price:  number;
  sale_price:     number | null;
  currency:       string;
  stock:          number;
  rating_avg:     number;
  rating_count:   number;
  is_featured:    boolean;
  is_trending:    boolean;
  is_new:         boolean;
  is_best_seller: boolean;
  status:         string;
  media:          MediaItem[];
  images:         MediaItem[];
  variants?:      Variants;
  tag?:           string | null;
  tagGold?:       boolean;
};

// ─── Normalisation helpers ────────────────────────────────────────────────

export function sortColorImages(images: VariantColorImage[] | undefined): VariantColorImage[] {
  if (!Array.isArray(images) || images.length === 0) return [];
  return [...images].sort((a, b) => {
    if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normaliseImages(p: any): MediaItem[] {
  const raw: any[] = Array.isArray(p.images) && p.images.length
    ? p.images
    : Array.isArray(p.media)
      ? p.media
      : [];

  return [...raw]
    .sort((a, b) => {
      const aPrimary = !!a.is_primary, bPrimary = !!b.is_primary;
      if (aPrimary !== bPrimary) return aPrimary ? -1 : 1;
      const aSort = Number(a.sort_order ?? 0), bSort = Number(b.sort_order ?? 0);
      if (aSort !== bSort) return aSort - bSort;
      return Number(a.id ?? 0) - Number(b.id ?? 0);
    })
    .map((m) => ({
      type: "img" as const,
      src: m.src ?? m.image_url,
      alt: m.alt ?? m.alt_text,
      is_primary: !!m.is_primary,
    }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normaliseVariants(p: any): Variants {
  const items: VariantItem[] = Array.isArray(p.variants?.items)
    ? p.variants.items.filter((it: VariantItem) => it.size && it.color)
    : [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawColors: any[] = Array.isArray(p.variants?.colors) ? p.variants.colors : [];
  const galleryByColorValue = new Map<string, VariantColorImage[]>();
  for (const c of rawColors) {
    const key = c?.value ?? c?.name;
    if (key) galleryByColorValue.set(key, sortColorImages(c.images));
  }

  if (items.length === 0) return { sizes: [], colors: [], items: [] };

  const sizeMap = new Map<string, number>();
  for (const it of items) sizeMap.set(it.size, (sizeMap.get(it.size) ?? 0) + (Number(it.stock_qty) || 0));
  const sizes: VariantSize[] = Array.from(sizeMap.entries()).map(([value, stock_qty]) => ({ value, stock_qty }));

  const colorMap = new Map<string, { hex: string; stock_qty: number }>();
  for (const it of items) {
    const prev = colorMap.get(it.color);
    colorMap.set(it.color, { hex: it.hex ?? prev?.hex ?? "#888888", stock_qty: (prev?.stock_qty ?? 0) + (Number(it.stock_qty) || 0) });
  }

  const colors: VariantColor[] = Array.from(colorMap.entries()).map(([value, { hex, stock_qty }]) => {
    const images = galleryByColorValue.get(value);
    return images && images.length ? { value, hex, stock_qty, images } : { value, hex, stock_qty };
  });

  return {
    sizes,
    colors,
    items: items.map((it) => ({ size: it.size, color: it.color, hex: it.hex ?? "#888888", stock_qty: Number(it.stock_qty) || 0 })),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normaliseProduct(p: any): Product {
  return {
    ...p,
    stock:          Number(p.stock ?? p.stock_qty ?? 0),
    is_featured:    !!p.is_featured,
    is_trending:    !!p.is_trending,
    is_new:         !!p.is_new,
    is_best_seller: !!p.is_best_seller,
    PRODUCT_TYPE_OPTIONS: p.PRODUCT_TYPE_OPTIONS ?? p.gender ?? null,
    gender:         p.PRODUCT_TYPE_OPTIONS || p.gender || null,
    media: (p.media ?? []).map((m: { src?: string; alt?: string; is_primary?: boolean }) => ({
      type: "img" as const, src: m.src, alt: m.alt, is_primary: !!m.is_primary,
    })),
    images:   normaliseImages(p),
    variants: normaliseVariants(p),
    tag:     p.is_new ? "New" : p.is_best_seller ? "Bestseller" : p.sale_price ? "Sale" : p.is_featured ? "Featured" : null,
    tagGold: p.is_new || p.is_best_seller || p.is_featured,
  };
}

export function pickDefaultVariant(variants: Variants | undefined): { color: string; size: string } {
  const colors = variants?.colors ?? [];
  const sizes  = variants?.sizes  ?? [];
  const items  = variants?.items  ?? [];

  const firstInStockColor = colors.find((c) => c.stock_qty > 0);
  const defaultColor = colors.length ? (firstInStockColor ?? colors[0]).value : "";

  let defaultSize = "";
  if (sizes.length) {
    if (defaultColor && items.length) {
      const sizeForColor = items.find((it) => it.color === defaultColor && it.stock_qty > 0);
      if (sizeForColor) defaultSize = sizeForColor.size;
    }
    if (!defaultSize) {
      const firstInStockSize = sizes.find((s) => s.stock_qty > 0);
      defaultSize = (firstInStockSize ?? sizes[0]).value;
    }
  }

  return { color: defaultColor, size: defaultSize };
}

export function resolveSizeForColor(variants: Variants | undefined, newColor: string, currentSize: string): string {
  const sizes = variants?.sizes ?? [];
  const items = variants?.items ?? [];
  if (!sizes.length) return "";

  if (items.length) {
    const stillValid = currentSize && items.some((it) => it.color === newColor && it.size === currentSize && it.stock_qty > 0);
    if (stillValid) return currentSize;

    const nextAvailable = items.find((it) => it.color === newColor && it.stock_qty > 0);
    if (nextAvailable) return nextAvailable.size;

    return "";
  }

  if (currentSize && sizes.some((s) => s.value === currentSize && s.stock_qty > 0)) return currentSize;
  const firstInStockSize = sizes.find((s) => s.stock_qty > 0);
  return firstInStockSize ? firstInStockSize.value : "";
}

// ─── Cart ─────────────────────────────────────────────────────────────────

export async function addToCartRequest(productId: number, size: string, color: string, qty: number) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/cart/add`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ product_id: productId, size: size || null, color: color || null, quantity: qty }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) throw new Error(json.message || "Failed to add to cart");
  return json;
}